import React from "react";
import ReactGA from "react-ga";
import { DefaultEnv } from "store/env";
import { stringify } from "qs";
import {
  generatePath,
  match,
  matchPath,
  RouteComponentProps,
  parse
} from "polyrouter";
import { withRouter } from "react-router";
import { State } from "store";
import { connect, MapStateToPropsParam } from "react-redux";
import { Pathname } from "history";
import { AllQueryParam } from "polyrouter/allQueryParams";
import { withAuth0, WithAuth0Props } from "@auth0/auth0-react";
import { hashCode } from "../utilities/hash";
import { computeUserProps } from "./computeUserProps";
const searchParam: AllQueryParam = "search";

const { googleAnalyticsId, isDataDogUser, verboseAnalyticsLogs } = DefaultEnv;
const shouldInitialize = !isDataDogUser && googleAnalyticsId;
if (shouldInitialize) {
  ReactGA.initialize(googleAnalyticsId, {
    gaOptions: {
      cookieFlags: "SameSite=None; Secure"
    }
  });
} else {
  ReactGA.initialize("invalid", {
    debug: verboseAnalyticsLogs,
    gaOptions: {
      cookieFlags: "SameSite=None; Secure"
    }
  });
  //Disable GA from sending any events.
  ReactGA.set({
    sendHitTask: null
  });
}

/**
 * Will attempt to locate the best matching parameterized route if one exists.
 * Best is defined by the longest route string with the most matched params.
 * So if a route is like /members/7/details is will return /members/:id/details before it would return /members/:id
 * and would return /members/:id/:subPage before either
 * @param routes
 * @param pathname
 */
export function isParamaterizedRoute(routes: string[], pathname: string) {
  const { match } = routes.reduce(
    (acc, path) => {
      const newMatch = matchPath(pathname, {
        path
      });
      if (!newMatch) {
        return acc;
      }
      const matchCount = Object.keys(newMatch.params).length;
      const hasAnyParams = matchCount > 0;
      if (!hasAnyParams) {
        return acc;
      }
      const { match, paramsCount } = acc;
      if (!match) {
        return { match: newMatch, paramsCount: matchCount };
      }
      const moreParams = matchCount > paramsCount;
      const equivalentParams = matchCount === paramsCount;
      const longerRoute = newMatch.path.length > match.path.length;
      const isBetterMatch = moreParams || (equivalentParams && longerRoute);
      if (isBetterMatch) {
        return { match: newMatch, paramsCount: matchCount };
      }
      return acc;
    },
    { match: null as match<any> | null, paramsCount: 0 }
  );
  if (!match) {
    return null;
  }
  return match;
}

export const generifyRoute = (
  fullPath: Pathname,
  { params, path, url, isExact }: match<any>
): string => {
  const anonParams = Object.keys(params).reduce((p, k) => {
    return {
      ...p,
      [k]: ":" + k
    };
  }, {});
  const anonPath = generatePath(path, anonParams);
  if (!isExact) {
    return fullPath.replace(url, anonPath);
  }
  return anonPath;
};
type RoutePropsForLogging = Pick<RouteComponentProps, "location">;
/**
 * Custom dimensions in google are names like dimension1... etc.
 * Its up to us to try to keep them in sync between environments.
 * Track that mapping here.
 */
type Dimension =
  | "dimension1"
  | "dimension2"
  | "dimension3"
  | "dimension4"
  | "dimension5"
  | "dimension6"
  | "dimension7"
  | "dimension8"
  | "dimension9"
  | "dimension10"
  | "dimension11"
  | "dimension12"
  | "dimension13"
  | "dimension14"
  | "dimension15"
  | "dimension16"
  | "dimension17"
  | "dimension18"
  | "dimension19"
  | "dimension20";
type DimensionFields =
  | "model"
  | "software_version"
  | "free2"
  | "domain"
  | "full_domain"
  | "user_id"
  | "location_scope"
  | "time_period"
  | "tenant_id";
export type LiveDimensions = Record<DimensionFields, Dimension>;
export type DimensionValues = Record<
  DimensionFields,
  string | null | undefined
>;

export const CustomDimensions: LiveDimensions = {
  model: "dimension1",
  software_version: "dimension2",
  free2: "dimension3",
  domain: "dimension4",
  full_domain: "dimension5",
  user_id: "dimension6",
  location_scope: "dimension7",
  time_period: "dimension8",
  tenant_id: "dimension9"
};

export type LoggingMethod = (
  { location }: RoutePropsForLogging,
  routes: string[]
) => void;
export const loggingMethod: LoggingMethod = (
  { location }: RoutePropsForLogging,
  routes: string[]
) => {
  const newSearch = parse(location.search);
  let qString = "";
  if (newSearch[searchParam]) {
    qString =
      "?" +
      stringify({
        [searchParam]: newSearch[searchParam]
      });
  }
  const paramPath = isParamaterizedRoute(routes, location.pathname);
  let page = location.pathname;
  let group = page;
  if (paramPath) {
    group = generifyRoute(page, paramPath);
  } else if (group === "/") {
    group = "/home";
  }
  page += qString;
  ReactGA.set({
    page,
    contentGroup5: group
  });
  ReactGA.pageview(page);
};

type Props = {
  routes: string[];
  logTransition: LoggingMethod;
};
type TrackerProps = RouteComponentProps & Props & WithAuth0Props;
export class UnconnectedLocationTracker extends React.PureComponent<
  TrackerProps
> {
  logPath = (routeProps: RoutePropsForLogging) => {
    const { routes, logTransition } = this.props;
    try {
      logTransition(routeProps, routes);
    } catch (e) {
      //Dunno what we can do if something like analytics fails, just not crash I guess.
    }
  };

  componentDidMount(): void {
    const { location } = this.props;
    this.logPath({ location });
  }

  updateLocation(
    location: RouteComponentProps["location"],
    nextLocation: RouteComponentProps["location"]
  ) {
    const newPath = location.pathname !== nextLocation.pathname;
    const newSearch = parse(nextLocation.search);
    const hasSearch = newSearch[searchParam];
    if (newPath || hasSearch) {
      this.logPath({ location: nextLocation });
      ReactGA.set({
        [CustomDimensions.tenant_id]: newSearch["tenantId"]
      });
    }
  }

  updateUser(prev: any | undefined, next: any | undefined) {
    if (!next && !prev) {
      return;
    }
    if (prev?.sub !== next?.sub) {
      let { full_domain, domain } = computeUserProps(next);
      ReactGA.set({
        [CustomDimensions.domain]: domain,
        [CustomDimensions.full_domain]: full_domain,
        [CustomDimensions.user_id]: next.sub
      });
      hashCode(next.sub).then(hash => {
        ReactGA.set({
          userId: hash
        });
      });
    }
  }

  componentDidUpdate(prevProps: Readonly<TrackerProps>): void {
    this.updateLocation(prevProps.location, this.props.location);
    this.updateUser(prevProps.auth0.user, this.props.auth0.user);
  }
  render() {
    return <></>;
  }
}
const mapStateToProps: MapStateToPropsParam<Props, {}, State> = (
  s: State
): Props => ({
  routes: s.route.paths,
  logTransition: loggingMethod
});
export const LocationTracker = connect<Props, {}, {}, State>(mapStateToProps)(
  withAuth0(withRouter(UnconnectedLocationTracker))
);
